import { gql, useQuery } from '@apollo/client';
import moment from 'moment';
import React, { lazy, Suspense } from 'react';
import AvatarStack from '../components/AvatarStack';
import RightPanel from '../components/RightPanel';
import Timestamp from '../components/Timestamp';
import type {
  PostInsightsQuery,
  PostInsightsQueryVariables,
} from '../graphql/generated/graphqlTypes';
import UserRow from '../user/UserRow';
import styles from './PostInsights.module.scss';
import PostInsightsBacklinks from './PostInsightsBacklinks';

const PostInsightsMetrics = lazy(
  () =>
    import(
      /* webpackChunkName: "postMetrics" */
      './PostInsightsMetrics'
    ),
);

const POST_INSIGHTS_QUERY = gql`
  query postInsights($id: ID!) {
    post(id: $id) {
      id
      owner {
        id
        ...UserRow_user
      }
      contributors {
        id
        ...AvatarStack_users
      }
      viewers {
        id
        ...AvatarStack_users
      }
      insertedAt
      updatedAt
      publishedAt
      ...PostInsightsBacklinks_post
    }
  }

  ${UserRow.fragments.user}
  ${AvatarStack.fragments.users}
  ${PostInsightsBacklinks.fragments.post}
`;

interface PostInsightsProps {
  close: () => void;
  id: string;
}

const PostInsights = ({ close, id }: PostInsightsProps) => {
  const { data } = useQuery<PostInsightsQuery, PostInsightsQueryVariables>(
    POST_INSIGHTS_QUERY,
    { variables: { id } },
  );

  return (
    <RightPanel header="Post insights" close={close}>
      {data ? (
        <>
          <Suspense fallback={null}>
            <PostInsightsMetrics id={id} />
          </Suspense>
          <div className={styles.ownerSection}>
            <div className={styles.sectionHeader}>owner</div>
            <UserRow link user={data.post.owner} avatarSize={32} />
          </div>
          <div className={styles.contributorsSection}>
            <div className={styles.sectionHeader}>contributors</div>
            <AvatarStack
              users={data.post.contributors}
              limit={5}
              link
              expandable
            />
          </div>
          <div className={styles.readersSection}>
            <div className={styles.sectionHeader}>readers</div>
            <AvatarStack
              users={data.post.viewers}
              limit={5}
              link
              tooltip="View readers"
              expandable
            />
          </div>
          <div className={styles.sectionGroup}>
            <div className={styles.section}>
              {data.post.publishedAt ? (
                <>
                  <div className={styles.sectionGroupHeader}>
                    date published
                  </div>
                  {moment(data.post.publishedAt).format('MMM D, YYYY')}
                </>
              ) : (
                <>
                  <div className={styles.sectionGroupHeader}>date created</div>
                  {moment(data.post.insertedAt).format('MMM D, YYYY')}
                </>
              )}
            </div>
            <div className={styles.section}>
              <div className={styles.sectionGroupHeader}>last updated</div>
              <Timestamp date={data.post.updatedAt} />
            </div>
          </div>
          {data.post.backlinks.length > 0 && (
            <div className={styles.backlinksSection}>
              <div className={styles.sectionHeader}>backlinks</div>
              <PostInsightsBacklinks post={data.post} showHoverCard />
            </div>
          )}
        </>
      ) : undefined}
    </RightPanel>
  );
};

export default PostInsights;
